import { useMemo, useState } from "react";
import { Reveal } from "@/components/Reveal";

const fmt = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const Slider = ({ label, value, min, max, step, onChange, testid, display }) => (
  <div>
    <div className="mb-3 flex items-center justify-between">
      <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">{label}</label>
      <span className="font-mono text-sm text-gold" data-testid={`${testid}-value`}>{display}</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      data-testid={testid}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full"
      style={{ "--fill": `${((value - min) / (max - min)) * 100}%` }}
    />
  </div>
);

export const Estimator = () => {
  const [monthly, setMonthly] = useState(500);
  const [years, setYears] = useState(20);
  const [taxRate, setTaxRate] = useState(24);

  const { taxFree, taxable, advantage } = useMemo(() => {
    const n = years * 12;
    const r = 0.06 / 12;
    const rt = (0.06 * (1 - taxRate / 100)) / 12;
    const fv = monthly * ((Math.pow(1 + r, n) - 1) / r);
    const fvt = monthly * ((Math.pow(1 + rt, n) - 1) / rt);
    return { taxFree: fv, taxable: fvt, advantage: fv - fvt };
  }, [monthly, years, taxRate]);

  return (
    <section id="estimator" data-testid="estimator-section" className="relative bg-[#0F091A] py-28 lg:py-40">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-2 lg:gap-24 lg:px-10">
        <div>
          <Reveal>
            <p className="eyebrow mb-6" data-testid="estimator-eyebrow">Interactive Tool</p>
            <h2 className="font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl lg:text-4xl">
              The Tax & Retirement
              <span className="text-gold-gradient"> Legacy Estimator</span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-white/55">
              See what a tax-advantaged structure could mean for your future. Move the sliders —
              then let us build the real blueprint.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-12 space-y-10">
              <Slider label="Monthly Contribution" value={monthly} min={100} max={3000} step={50}
                onChange={setMonthly} testid="estimator-monthly-slider" display={fmt(monthly)} />
              <Slider label="Years to Retirement" value={years} min={5} max={40} step={1}
                onChange={setYears} testid="estimator-years-slider" display={`${years} yrs`} />
              <Slider label="Current Tax Bracket" value={taxRate} min={10} max={37} step={1}
                onChange={setTaxRate} testid="estimator-tax-slider" display={`${taxRate}%`} />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="flex items-center">
          <div className="gold-border-glow w-full bg-wine/80 p-10" data-testid="estimator-results-card">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
              Projected at 6% annual growth
            </p>
            <div className="mt-8 space-y-8">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold/80">
                  Tax-Advantaged Structure
                </p>
                <p className="mt-2 font-display text-4xl font-semibold text-gold-gradient" data-testid="estimator-result-taxfree">
                  {fmt(taxFree)}
                </p>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                  Taxable Account Equivalent
                </p>
                <p className="mt-2 font-display text-2xl font-medium text-white/60" data-testid="estimator-result-taxable">
                  {fmt(taxable)}
                </p>
              </div>
              <div className="border-t border-gold/15 pt-8">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold/80">
                  Your Tax-Efficiency Advantage
                </p>
                <p className="mt-2 font-display text-3xl font-semibold text-white" data-testid="estimator-result-advantage">
                  +{fmt(advantage)}
                </p>
              </div>
            </div>
            <p className="mt-10 text-xs leading-relaxed text-white/35" data-testid="estimator-disclaimer">
              Illustrative projection only — not financial, tax, or investment advice. Actual results
              vary by product, market performance, and individual circumstances.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
